import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CODE_LANGUAGES, CODE_SAMPLES, CodeLanguage } from '../../data/tool/code-image.data';
import { CODE_IMAGE_THEMES, CodeImageTheme } from '../../data/theme/code-image-themes.data';
import { THEMES as PRODUCT_THEMES } from '../../data/product/product.data';
import { AnalyticsService } from '../../service/analytics/analytics.service';
import { CodeBackdrop, measureCodeImage, paintCodeImage } from '../../service/code-image/code-image.painter';
import { canPrettify, prettify } from '../../service/code-image/code-image.prettify';
import { CodeImageState, codeImageOptions, defaultCodeImageState } from '../../service/code-image/code-image.state';

/**
 * The code snippet image generator's card (2026-10-13), on its tool page and in the home's Web Tools box: the
 * code beside its picture, and the settings under them. The card keeps the settings and draws the preview;
 * every change goes out as `stateChange`, so the tool page draws the full size picture, the colour vision
 * pictures and the download from the same settings (code-image.state.ts).
 */
@Component( {
  selector: 'app-code-image-widget',
  templateUrl: './code-image-widget.component.html',
  styleUrls: [ './code-image-widget.component.scss' ],
} )
export class CodeImageWidgetComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  /** the theme and the language to open on (the tool page's ?theme= and ?lang=) */
  @Input() themeKey = '';
  @Input() languageKey = '';
  /** what the analytics events say they came from */
  @Input() source = 'tool';

  @Output() stateChange = new EventEmitter<CodeImageState>();
  /** the theme or the language was picked here (the tool page keeps its url in step) */
  @Output() pick = new EventEmitter<CodeImageState>();
  /** the preview's badge was pressed */
  @Output() previewClick = new EventEmitter<void>();

  LANGUAGES = CODE_LANGUAGES;
  /** each theme's own icon (the round logo of the theme), for the theme picker (owner 2026-10-13) */
  readonly THEME_LOGOS: { [ key: string ]: string } = PRODUCT_THEMES
    .reduce( ( all, line ) => all.concat( line.products ), [] as { key: string; logo: string }[] )
    .reduce( ( map, p ) => ( { ...map, [ p.key ]: p.logo } ), {} as { [ key: string ]: string } );

  /** each theme's five colours, under its name in the theme picker as in the Appearance menu (owner 2026-10-13) */
  readonly THEME_COLORS: { [ key: string ]: string[] } = PRODUCT_THEMES
    .reduce( ( all, line ) => all.concat( line.products ), [] as { key: string; compactPalette?: string[] }[] )
    .reduce( ( map, p ) => ( { ...map, [ p.key ]: ( p.compactPalette || [] ).slice( 0, 5 ) } ), {} as { [ key: string ]: string[] } );

  /** the theme picker's rows in the Appearance menu's order (owner 2026-10-13): Nature then Cities, each with its
   *  light themes then its dark ones, in the site's own order */
  readonly THEME_GROUPS: { name: string; minor: { name: string; themes: CodeImageTheme[] }[] }[] = [ 'nature', 'cities' ]
    .map( key => PRODUCT_THEMES.find( line => line.key === key ) )
    .filter( line => !!line )
    .map( line => ( {
      name: line!.name,
      minor: [ { name: 'Light', light: true }, { name: 'Dark', light: false } ].map( group => ( {
        name: group.name,
        themes: line!.products
          .filter( p => !!p.appearance && p.appearance.startsWith( 'Light' ) === group.light )
          .map( p => CODE_IMAGE_THEMES.find( t => t.key === p.key ) )
          .filter( ( t ): t is CodeImageTheme => !!t ),
      } ) ).filter( group => group.themes.length > 0 ),
    } ) );
  BACKDROPS: { key: CodeBackdrop; label: string }[] = [
    { key: 'gradient', label: 'Gradient' },
    { key: 'solid', label: 'Solid' },
    { key: 'none', label: 'None' },
  ];
  PADDINGS = [ 16, 32, 64, 96 ];

  @ViewChild( 'preview' ) previewRef?: ElementRef<HTMLCanvasElement>;
  @ViewChild( 'previewWell' ) previewWellRef?: ElementRef<HTMLElement>;

  state: CodeImageState = defaultCodeImageState();
  /** the picture is much taller than the preview's well (a long paste): it scrolls there instead of being cropped */
  tallPreview = false;

  /** the title still follows the language (snippet.ts, snippet.py) until it is typed in */
  private titleTouched = false;
  private paintQueued = false;
  private destroyed = false;

  constructor( private analyticsService: AnalyticsService, private zone: NgZone ) {
  }

  ngOnInit(): void {
    // the other grammars come in their own chunk; the picture is drawn again when they are here
    import( '../../service/code-image/code-image.languages' ).then( () => this.changed() ).catch( () => undefined );
  }

  ngOnChanges( changes: SimpleChanges ): void {
    if ( changes[ 'themeKey' ] || changes[ 'languageKey' ] ) {
      const theme = CODE_IMAGE_THEMES.find( t => t.key === this.themeKey );
      const language = CODE_LANGUAGES.find( l => l.key === this.languageKey );
      if ( theme && theme !== this.state.theme ) {
        this.state = { ...this.state, theme };
      }
      if ( language && language !== this.state.language ) {
        this.applyLanguage( language );
      }
      this.changed();
    }
  }

  ngAfterViewInit(): void {
    this.changed();
    // the picture is measured in the code font: draw again once the fonts are in
    const fonts = ( document as any ).fonts;
    if ( fonts && fonts.ready ) {
      fonts.ready.then( () => this.changed() );
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    clearTimeout( this.prettifyTimer );
  }

  // ---------------------------------------------------------------- the controls

  onCode( value: string ): void {
    this.state = { ...this.state, code: value };
    this.changed();
  }

  // ----- Prettify (owner 2026-10-13): Prettier, loaded on the first press, for the languages it knows -----

  prettifyState: '' | 'busy' | 'failed' = '';
  private prettifyTimer: any = null;

  get canPrettify(): boolean {
    return canPrettify( this.state.language.key );
  }

  prettifyCode(): void {
    if ( !this.canPrettify || this.prettifyState === 'busy' || !this.state.code.trim() ) {
      return;
    }
    this.prettifyState = 'busy';
    prettify( this.state.code, this.state.language.key ).then( out => this.zone.run( () => {
      this.prettifyState = '';
      this.onCode( out );
      this.analyticsService.logEvent( 'Code Snippet Prettified', { language: this.state.language.key, source: this.source } );
    } ) ).catch( () => this.zone.run( () => {
      // code that does not parse is left as it is; the button says so for a moment
      this.prettifyState = 'failed';
      clearTimeout( this.prettifyTimer );
      this.prettifyTimer = setTimeout( () => ( this.prettifyState = '' ), 1800 );
    } ) );
  }

  /** Tab types two spaces instead of leaving the editor. */
  onEditorKey( event: KeyboardEvent ): void {
    if ( event.key !== 'Tab' || event.shiftKey || event.metaKey || event.ctrlKey || event.altKey ) {
      return;
    }
    event.preventDefault();
    const area = event.target as HTMLTextAreaElement;
    const start = area.selectionStart;
    area.value = area.value.slice( 0, start ) + '  ' + area.value.slice( area.selectionEnd );
    area.selectionStart = area.selectionEnd = start + 2;
    this.onCode( area.value );
  }

  setLanguage( key: string ): void {
    const language = CODE_LANGUAGES.find( l => l.key === key );
    if ( !language || language === this.state.language ) {
      return;
    }
    this.applyLanguage( language );
    this.changed();
    this.pick.emit( this.state );
    this.analyticsService.logEvent( 'Code Snippet Language Changed', { language: language.key, source: this.source } );
  }

  /** The language, its sample when the code on the page is still a sample, and the title's extension. */
  private applyLanguage( language: CodeLanguage ): void {
    const code = this.state.code;
    const untouched = !code.trim() || Object.keys( CODE_SAMPLES ).some( k => CODE_SAMPLES[ k ] === code );
    this.state = {
      ...this.state,
      language,
      code: untouched && CODE_SAMPLES[ language.key ] ? CODE_SAMPLES[ language.key ] : code,
      title: this.titleTouched ? this.state.title : 'snippet.' + language.ext,
    };
  }

  // ----- the theme picker (owner 2026-10-13): the tools' visual picker, each theme with its icon -----

  themeOpen = false;
  themeMaxHeight = 440;

  toggleThemePick( button?: HTMLElement ): void {
    this.themeOpen = !this.themeOpen;
    if ( this.themeOpen && button && typeof window !== 'undefined' ) {
      this.themeMaxHeight = Math.max( 260, Math.round( window.innerHeight - button.getBoundingClientRect().bottom - 8 - 16 ) );
    }
  }

  pickTheme( key: string ): void {
    this.themeOpen = false;
    const theme = CODE_IMAGE_THEMES.find( t => t.key === key );
    if ( !theme ) {
      return;
    }
    this.state = { ...this.state, theme };
    this.changed();
    this.pick.emit( this.state );
    this.analyticsService.logEvent( 'Code Snippet Theme Changed', { theme: theme.key, from: 'select', source: this.source } );
  }

  @HostListener( 'document:click', [ '$event' ] )
  onDocumentClick( event: MouseEvent ): void {
    if ( this.themeOpen && !( event.target as HTMLElement ).closest( '.ci-theme-pick' ) ) {
      this.themeOpen = false;
    }
  }

  @HostListener( 'document:keydown.escape' )
  onEscape(): void {
    this.themeOpen = false;
  }

  setBackdrop( key: string ): void {
    this.state = { ...this.state, backdrop: key as CodeBackdrop };
    this.changed();
  }

  setPadding( value: string | number ): void {
    this.state = { ...this.state, padding: Number( value ) || 0 };
    this.changed();
  }

  setTitle( value: string ): void {
    this.titleTouched = true;
    this.state = { ...this.state, title: value };
    this.changed();
  }

  toggleLineNumbers(): void {
    this.state = { ...this.state, lineNumbers: !this.state.lineNumbers };
    this.changed();
  }

  toggleWindowBar(): void {
    this.state = { ...this.state, windowBar: !this.state.windowBar };
    this.changed();
  }

  // ---------------------------------------------------------------- the picture

  /** the hosts hear of every change; the preview is drawn on the next frame */
  private changed(): void {
    if ( this.destroyed ) {
      return;
    }
    // after this check (the hosts show the theme and the language in their own templates)
    Promise.resolve().then( () => this.destroyed || this.stateChange.emit( this.state ) );
    if ( this.paintQueued || typeof requestAnimationFrame === 'undefined' ) {
      return;
    }
    this.paintQueued = true;
    requestAnimationFrame( () => {
      this.paintQueued = false;
      this.paint();
    } );
  }

  paint(): void {
    const canvas = this.previewRef?.nativeElement;
    const ctx = canvas?.getContext( '2d' );
    if ( !canvas || !ctx ) {
      return;
    }
    const scale = Math.min( 2, Math.max( 1, Math.round( window.devicePixelRatio || 1 ) ) );
    const options = codeImageOptions( this.state, scale );
    // the picture's own size (what the download is), then, for the preview only, at least as tall as the well's
    // shape: an empty or short snippet fills the well with its backdrop, never cropped (owner 2026-10-13)
    const own = measureCodeImage( ctx, options );
    const well = this.previewWellRef?.nativeElement;
    const wellRatio = well && well.clientWidth > 0 ? well.clientHeight / well.clientWidth : 0;
    if ( wellRatio > 0 ) {
      options.minHeight = Math.ceil( own.width * wellRatio );
    }
    paintCodeImage( canvas, options );
    const width = canvas.width / scale;
    const height = canvas.height / scale;
    canvas.style.width = width + 'px';
    canvas.style.aspectRatio = width + ' / ' + height;
    // a long paste is taller than the well: it scrolls there instead of being cropped
    const tall = wellRatio > 0 && height / width > wellRatio * 1.25;
    if ( tall !== this.tallPreview ) {
      this.zone.run( () => ( this.tallPreview = tall ) );
    }
  }
}
